import type { SubsidyChoices } from "@/features/cart/types/cart.type";
import type {
  DealAssignee,
  DealAssigneesResponse,
  DealAssignmentPayload,
} from "@/features/deals/types/dealAssignment.type";
import type { DealCart, DealCartResponse } from "@/features/deals/types/dealCart.type";
import type { DealOrder } from "@/features/deals/types/dealOrder.type";
import { fetcher } from "@/shared/lib/fetcher.client";

/**
 * Response type for cart confirmation
 * Contains the full order object returned by the API
 */
export type DealCartConfirmResponse = { order: DealOrder };

/**
 * Response type for cart payment URL
 */
export type DealCartPaymentResponse = { payment_url?: string };

/**
 * Deals cart service for managing the deals shopping cart
 */
class DealsCartService {
  /**
   * Fetch the current deals cart
   */
  async getCart(): Promise<DealCart> {
    const response = await fetcher.get<DealCartResponse>("/deals/cart");
    return response.data.cart;
  }

  /**
   * Add an item to the deals cart
   */
  async addToCart(referenceId: string, quantity: number): Promise<DealCart> {
    const response = await fetcher.put<DealCartResponse>(
      "/deals/cart/items",
      null,
      { params: { reference_id: referenceId, quantity } },
    );
    return response.data.cart;
  }

  /**
   * Update an item quantity in the deals cart
   */
  async updateCartItem(itemId: string, quantity: number): Promise<DealCart> {
    const response = await fetcher.put<DealCartResponse>(
      `/deals/cart/items/${itemId}`,
      { quantity },
    );
    return response.data.cart;
  }

  /**
   * Remove an item from the deals cart
   */
  async removeCartItem(referenceId: string): Promise<DealCart> {
    const response = await fetcher.delete<DealCartResponse>(
      "/deals/cart/items",
      { params: { reference_id: referenceId } },
    );
    return response.data.cart;
  }

  /**
   * Clear all items from the deals cart
   * Note: API returns 204 No Content, so no response body
   */
  async clearCart(): Promise<void> {
    await fetcher.delete("/deals/cart");
  }

  /**
   * Attach a shipping address to the deals cart
   * Returns the cart with assignments_complete flag
   */
  async setShippingAddress(shippingAddressId: number): Promise<DealCart> {
    const response = await fetcher.put<DealCartResponse>(
      "/deals/cart/shipping_address",
      { shipping_address_id: shippingAddressId },
    );
    return response.data.cart;
  }

  /**
   * Fetch available assignees for a deal reference
   * Flattens the employees and beneficiaries arrays into a single array
   */
  async getAssignees(referenceId: string): Promise<DealAssignee[]> {
    const response = await fetcher.get<DealAssigneesResponse>(
      `/deals/references/${referenceId}/assignees`,
    );
    const { employees, beneficiaries } = response.data;
    return [...employees, ...beneficiaries];
  }

  /**
   * Assign a person to a cart item
   */
  async assignCartItem(
    itemId: string,
    payload: DealAssignmentPayload,
  ): Promise<DealCart> {
    const response = await fetcher.put<DealCartResponse>(
      `/deals/cart/items/${itemId}/assignment`,
      payload,
    );
    return response.data.cart;
  }

  /**
   * Update subsidy choices for the deals cart
   */
  async updateSubsidyChoices(payload: SubsidyChoices): Promise<DealCart> {
    const response = await fetcher.patch<DealCartResponse>(
      "/deals/cart/subsidy_choices",
      { subsidy_choices: payload },
    );
    return response.data.cart;
  }

  /**
   * Confirm/validate the deals cart before payment
   */
  async confirmCart(): Promise<DealCartConfirmResponse> {
    const response = await fetcher.post<DealCartConfirmResponse>(
      "/deals/cart/confirm",
    );
    return response.data;
  }

  /**
   * Get the payment URL for the confirmed deals cart
   */
  async getPaymentUrl(): Promise<DealCartPaymentResponse> {
    const response = await fetcher.post<DealCartPaymentResponse>(
      "/deals/cart/payment",
    );
    return response.data;
  }
}

export const dealsCartService = new DealsCartService();
